import { Link, useNavigate } from "@tanstack/react-router";
import { Icon, StaticTooltipHost, tokens } from "@vintecc/components";
import classNames from "classnames";
import { useDefaultApps } from "@/hooks/useDefaultApps";
import styles from "./Navigation.module.css";
import { Loader } from "../Loader/Loader";
import { StyledImage } from "@/shared/components/StyledImageComponent/StyledImageComponent";
import { usePlatformAdminMutations } from "@/services/platformAdmin";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { marketPlaceQueries } from "@/services/marketPlace";
import { useQuery } from "@tanstack/react-query";
import { NavItem } from "./NavItem";
import { faGrid } from "@vintecc/pro-solid-svg-icons/faGrid";
import { useBreakpoint } from "@/shared/hooks/useBreakpoint";
import { useCommonTranslation } from "@/services/culture/common.translations";

const Navigation = () => {
  const { t } = useCommonTranslation();
  const defaultApps = useDefaultApps();
  const navigate = useNavigate();
  const { isMd } = useBreakpoint();
  const { navigate: navigateMutation } = usePlatformAdminMutations();
  const { data: customApps } = useQuery(marketPlaceQueries.getCustomAppsForUser());

  const handleCustomAppNavigation = (appId: number, platformApp: string) => {
    navigateMutation.mutate({ location: platformApp, app: true, appId });
    navigate({ to: "/app/$id", params: { id: platformApp.toLowerCase() } });
  };

  return (
    <div className={classNames(styles.root, "w-full md:w-16 h-full relative z-20 py-0 md:py-6 [background:var(--background-navigation)]")}>
      <div
        className={classNames(
          styles.content,
          "flex md:flex-col justify-start items-center gap-2 md:gap-4 overflow-auto w-full md:w-auto md:h-full py-2 md:py-0 pe-2 md:pe-0 snap-x md:snap-y md:scroll-pt-14 md:[scrollbar-width:none]",
        )}
      >
        <Link to="/" className="logo sticky top-0 z-10 hidden md:block">
          <StyledImage type="emblem" fallBack="/emblem.svg" width={46} height={46} loader={<Loader />} />
        </Link>

        <StaticTooltipHost
          tooltip={<span className="font-semibold">{t("navigation.applications")}</span>}
          customStyling={{ fill: "var(--background-navigation)", textColor: tokens.color.white[100] }}
          radixProps={{ contentProps: { sideOffset: 8 } }}
          place={isMd ? "right" : "top"}
        >
          <Link
            to="/"
            style={{ ["--color" as string]: tokens.color.blue[300] }}
            className="ms-2 md:ms-0 h-12 w-12 flex items-center justify-center text-(--navigation-item-color) relative shrink-0 snap-start before:absolute before:inset-0 before:rounded-xl before:transition-colors before:-z-1"
            aria-label={t("navigation.applications")}
          >
            <Icon icon={faGrid} size="2xl" />
          </Link>
        </StaticTooltipHost>

        <div className="flex md:grid gap-2">
          {defaultApps.map((app, index) => {
            if (!app.showInNavigation) return null;
            if (!app.visible) return null;
            if (!app.to) return null;

            return (
              <NavItem
                key={`${app.title}-${index}`}
                tooltipLabel={app.title}
                to={app.to}
                search={app.searchParams}
                color={app.color}
                icon={app.icon}
                fallbackInitials={app.title.substring(0, 2)}
                tooltipPlace={isMd ? "right" : "top"}
                onClick={() => app.onClick?.()}
              />
            );
          })}

          {(customApps?.data || []).map((app, index) => {
            return (
              <NavItem
                key={`${app.platformApp}-${index}`}
                tooltipLabel={app.platformApp || ""}
                to="/app/$id"
                params={{ id: app.platformApp!.toLowerCase() }}
                color={app.color}
                icon={app.icon ? <Icon icon={["fal", app.icon as IconName]} /> : undefined}
                fallbackInitials={app.platformApp!.substring(0, 2)}
                tooltipPlace={isMd ? "right" : "top"}
                onClick={() => {
                  handleCustomAppNavigation(app.platformAppId!, app.platformApp!);
                }}
              />
            );
          })}
        </div>
      </div>

      {/* top triangle */}
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-[-10px] md:top-0 inset-e-0 md:inset-e-[-10px] rotate-180 md:rotate-0"
      >
        <path d="M0 0V10C0 10 0 0 10 0H0Z" fill="#25253E" />
      </svg>

      {/* bottom triangle */}
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-[-10px] md:top-auto md:bottom-0 inset-e-0 inset-s-0 md:inset-s-auto md:inset-e-[-10px]"
      >
        <path d="M0 10L10 10C10 10 0 10 1.19249e-07 0L0 10Z" fill="#25253E" />
      </svg>
    </div>
  );
};

export default Navigation;
